import { Edit } from '@mui/icons-material';
import { ButtonGroup } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@untitled-ui/icons-react/build/esm/ChevronLeft';
import ChevronRightIcon from '@untitled-ui/icons-react/build/esm/ChevronRight';
import { format } from 'date-fns';
import type { CSSProperties, ChangeEvent, FC, ReactNode } from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import useCurrentUser from 'src/hooks/use-current-user';
import { permissions } from 'src/paths';
import type { CalendarView } from 'src/types/calendar';

interface ViewOption {
  label: string;
  value: CalendarView;
}

const { UPDATE_AVAILABILITY } = permissions;

const viewOptions: ViewOption[] = [
  {
    label: 'Month',
    value: 'dayGridMonth',
  },
  {
    label: 'Week',
    value: 'timeGridWeek',
  },
  {
    label: 'Day',
    value: 'timeGridDay',
  },
  {
    label: 'Agenda',
    value: 'listWeek',
  },
];

interface CalendarToolbarProps {
  children?: ReactNode;
  date: Date;
  onAddClick?: () => void;
  onDateNext?: () => void;
  onDatePrev?: () => void;
  onDateToday?: () => void;
  onViewChange?: (view: CalendarView) => void;
  view: CalendarView;
  editMode?: boolean;
  close?: any;
  saveChanges?: any;
  getWeekRange?: any;
  errorInGet?: any;
  selectedTherapist?: any;
}

export const CalendarToolbar: FC<CalendarToolbarProps> = (props) => {
  const {
    date,
    onAddClick,
    onDateNext,
    onDatePrev,
    onDateToday,
    onViewChange,
    editMode = false,
    view,
    saveChanges,
    close,
    getWeekRange,
    errorInGet,
    selectedTherapist,
    ...other
  } = props;

  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );

  const [providerId] = useCurrentUser(['providerId']);

  const handleViewChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      onViewChange?.(event.target.value as CalendarView);
    },
    [onViewChange]
  );

  const dateMonth = format(date, 'MMMM');
  const dateDay = format(date, 'y');

  const syntheticEventMonth: ChangeEvent<HTMLInputElement> = {
    target: {
      value: viewOptions[0].value,
    },
  } as ChangeEvent<HTMLInputElement>;

  const syntheticEventWeek: ChangeEvent<HTMLInputElement> = {
    target: {
      value: viewOptions[1].value,
    },
  } as ChangeEvent<HTMLInputElement>;

  const getMonthName = (date: string) => {
    const monthNumber = new Date(date).getMonth();
    const options: any = { month: 'long' };
    const monthName = new Date(0, monthNumber, 1).toLocaleString('en-US', options);
    return monthName;
  };

  const startYear = new Date(getWeekRange.start).getFullYear();
  const endYear = new Date(getWeekRange.end).getFullYear();
  const startDay = new Date(getWeekRange.start).getDate();
  const endDay = new Date(getWeekRange.end).getDate();
  const startMonthName = getMonthName(getWeekRange.start);
  const endMonthName = getMonthName(getWeekRange.end);

  const styleForYear: CSSProperties = {
    color: '#6C737F',
    fontWeight: 700,
  };

  const styleForMonth: CSSProperties = {
    fontWeight: 400,
    color: '#6C737F',
  };
  const monthAbbreviations = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const renderWeekView = () => {
    if (startYear !== endYear) {
      return (
        <>
          <span style={styleForMonth}>{`${startDay} ${
            monthAbbreviations[new Date(startMonthName + ' 1, 2000')?.getMonth()]
          } `}</span>
          <span style={styleForYear}>{startYear}</span>
          <span style={styleForMonth}>{`-${endDay} ${
            monthAbbreviations[new Date(endMonthName + ' 1, 2000')?.getMonth()]
          } `}</span>
          <span style={styleForYear}>{endYear}</span>
        </>
      );
    } else if (startMonthName !== endMonthName) {
      return (
        <>
          <span style={styleForMonth}>
            {`${startDay} ${startMonthName}-${endDay} ${endMonthName} `}
          </span>
          <span style={styleForYear}>{startYear}</span>
        </>
      );
    } else {
      return (
        <>
          <span style={styleForMonth}>{`${startDay}-${endDay} ${startMonthName} `}</span>
          <span style={styleForYear}>{startYear}</span>
        </>
      );
    }
  };

  const renderDayView = () => (
    <>
      <span style={styleForMonth}>{dateMonth}</span> <span style={styleForYear}>{dateDay}</span>
    </>
  );

  return (
    <Stack
      alignItems="center"
      flexWrap="wrap"
      justifyContent="space-between"
      flexDirection={{
        xs: 'column',
        md: 'row',
      }}
      {...other}
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing={1}
      >
        <IconButton
          sx={{
            ':hover': {
              background: '#EBEEFE',
            },
          }}
          onClick={onDatePrev}
        >
          <SvgIcon>
            <ChevronLeftIcon />
          </SvgIcon>
        </IconButton>
        <Typography
          noWrap
          variant="h5"
        >
          {view === 'timeGridWeek' ? renderWeekView() : renderDayView()}
        </Typography>

        <IconButton
          sx={{
            ':hover': {
              background: '#EBEEFE',
            },
          }}
          onClick={onDateNext}
        >
          <SvgIcon>
            <ChevronRightIcon />
          </SvgIcon>
        </IconButton>
      </Stack>
      <Stack
        alignItems="center"
        direction="row"
        spacing={'12px'}
      >
        {!editMode && (
          <>
            <Button
              variant="outlined"
              sx={{
                color: '#6366F1',
                '&:hover': {
                  background: 'transparent',
                },
                height: '36px',
              }}
              onClick={onDateToday}
            >
              Today
            </Button>
            <ButtonGroup
              variant="contained"
              sx={{
                border: '1px solid rgba(99, 102, 241, 0.50)',
                borderRadius: '12px',
                height: '36px',
              }}
            >
              <Button
                onClick={() => handleViewChange(syntheticEventWeek)}
                sx={{
                  color: '#6366F1',
                  background: view === viewOptions[1].value ? '#EBEEFE' : '#FFFFFF',
                  '&:hover': {
                    background: 'transparent',
                  },
                }}
              >
                Week
              </Button>
              <Button
                onClick={() => handleViewChange(syntheticEventMonth)}
                sx={{
                  color: '#6366F1',
                  background: view === viewOptions[0].value ? '#EBEEFE' : '#FFFFFF',
                  '&:hover': {
                    background: 'transparent',
                  },
                }}
              >
                Month
              </Button>
            </ButtonGroup>
          </>
        )}

        {editMode && (
          <Button
            variant="outlined"
            onClick={() => close()}
            sx={{ height: '36px' }}
          >
            Cancel
          </Button>
        )}

        {!editMode ? (
          <Button
            onClick={onAddClick}
            startIcon={
              <SvgIcon>
                <Edit />
              </SvgIcon>
            }
            sx={{
              width: {
                xs: '100%',
                md: 'auto',
              },
              height: '36px',
              '&.Mui-disabled': {
                pointerEvents: 'auto',
              },
            }}
            variant="contained"
            title={errorInGet?.isError ? errorInGet?.message : null}
            disabled={!!errorInGet?.isError || !permissionData?.includes(UPDATE_AVAILABILITY)}
          >
            {!selectedTherapist || providerId === selectedTherapist?.uuid
              ? 'Edit My Availability'
              : 'Edit Availability'}
          </Button>
        ) : (
          <Button
            onClick={saveChanges}
            variant="contained"
            sx={{ height: '36px' }}
          >
            Save changes
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
